// Re-export NotificationChannelType as ChannelType for backwards compatibility
// Canonical source: src/pages/alerts/types.ts
export { NotificationChannelType as ChannelType } from "@/pages/alerts/types";

export const SEARCH_PARAMS = {
  PAGE: "page",
  PAGE_SIZE: "page_size",
  CATEGORY: "category",
  RULE_TYPE: "rule_type",
  PLATFORM: "platform",
  CREATED_BY: "created_by",
  DESTINATION: "destination",
  SEARCH: "search",
  SORT_BY: "sort_by",
  SORT: "sort",
  START_DATE: "start_date",
  END_DATE: "end_date",
  STATUS: "status",
};

export const TIME_PERIODS = [
  { label: "Last Day", period: 1 },
  { label: "Last Week", period: 7 },
  { label: "Last Month", period: 30 },
];

// Enums for type safety

/** Subscription types */
export enum SubscriptionType {
  REPORT = "report",
  ALERT = "alert",
}

/** Wizard step identifiers */
export enum WizardStepEnum {
  SUBSCRIPTION_TYPE = "subscription-type",
  CUSTOMIZE = "customize",
  DESTINATION = "destination",
}

/** Subscription status values */
export enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

/** Run/execution status values for subscription history.
 *
 * Alert subscriptions: TRIGGERED (condition met) or SKIPPED (condition not met).
 * Report subscriptions: SENT (delivered).
 * Either: FAILURE (execution error).
 */
export enum RunStatus {
  FAILURE = "failure",
  TRIGGERED = "triggered",
  SKIPPED = "skipped",
  SENT = "sent",
}

/** History dropdown filter values.
 *
 * Superset of `RunStatus` plus `ALL` (no filter). Used by the History
 * status dropdown (`HistorySection`), the URL `?status=` validator
 * (`useSubscriptionDetail`), and any other site that drives the
 * history filter UI. Concrete-status values are intentionally the same
 * strings as `RunStatus` so the discriminator can be compared against
 * either enum without coercion.
 */
export enum StatusFilter {
  ALL = "all",
  TRIGGERED = "triggered",
  SKIPPED = "skipped",
  SENT = "sent",
  FAILURE = "failure",
}

/** Variable types used in subscription prompts */
export enum VariableType {
  AGGREGATION = "aggregation",
  DATE_RANGE = "date_range",
  COMPARISON = "comparison",
  CHANGE_TYPE = "change_type",
  THRESHOLD = "threshold",
  METRIC = "metric",
  ENTITY = "entity",
  SCHEDULE = "schedule",
  FILTER = "filter",
}

/** Preview item content types */
export enum PreviewItemType {
  MARKDOWN = "markdown",
  CHART = "chart",
}

/** Change type values for alerts */
export enum ChangeType {
  RELATIVE_VALUE = "relative_value",
  ABSOLUTE_VALUE = "absolute_value",
}

/** Report type restrictions — reports only support these specific options per source. */
export const REPORT_RESTRICTIONS = {
  BY_SOURCE: {
    snowflake: {
      ALLOWED_CATEGORIES: ["account"],
      ALLOWED_METRICS: ["account_overall_cost"],
      ALLOWED_FILTERS: ["instance"],
    },
    databricks: {
      ALLOWED_CATEGORIES: ["account"],
      ALLOWED_METRICS: ["dbx_account_total_cost"],
      ALLOWED_FILTERS: ["instance"],
    },
  },
} as const;

/** Comparison type values for alerts */
export enum ComparisonType {
  CHANGES_BY = "changes_by",
  EXCEEDS = "exceeds",
  BELOW = "below",
  CHANGES_BY_PERCENT = "changes_by_percent",
}

/** Date range values for subscriptions */
export enum DateRange {
  PREVIOUS_DAY = "previous_day",
  PREVIOUS_WEEK = "previous_week",
  PREVIOUS_MONTH = "previous_month",
  PREVIOUS_YEAR = "previous_year",
  PREVIOUS_DAY_TRAILING_7_DAY_AVG = "previous_day_trailing_7_day_avg",
}
